import { useCallback, useState } from "react";

/**
 * Řekne, jestli je obrázek na dané URL už stažený — podklad pro ghost načítání.
 *
 * AIDEV-NOTE: Stav se drží jako URL, ne jako boolean. Obrázek z cache stihne `load`
 * dřív, než React připojí handler, takže samotné `onLoad` by ghost nechalo svítit
 * navždy; proto `ref` navíc čte `img.complete`. Boolean + resetovací efekt tady
 * nefunguje — ref callback běží při commitu PŘED efektem, takže by ho efekt hned
 * přepsal zpět na false. Párování s URL řeší obojí a je to týž vzor jako dvojice
 * `{ path, url }` v `useAssetUrlResolver`. [TD-349]
 */
export const useImageLoaded = (url: string | null) => {
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);

  const ref = useCallback(
    (node: HTMLImageElement | null) => {
      // `complete` je true i pro obrázek, který se načetl z cache mezi renderem a commitem.
      if (node?.complete === true && url !== null) setLoadedUrl(url);
    },
    [url],
  );

  const onLoad = useCallback(() => {
    if (url !== null) setLoadedUrl(url);
  }, [url]);

  // Rozbitá cesta ve Storage (404, smazaný soubor) `load` nikdy nevyvolá. Bez tohohle
  // by ghost u takové fotky blikal donekonečna; takhle přestane a zůstane, co zbylo.
  const onError = onLoad;

  // Po přepnutí knihy nesmí zůstat „načteno" z předchozí URL — jinak by se ghost
  // přeskočil a na okamžik svítila obálka cizí knihy.
  return { isLoaded: url !== null && loadedUrl === url, ref, onLoad, onError };
};
